/* AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useState } from "react";
import ReactSelectSearch from "../Select/ReactSelectSearch";
import { OptionTypeBase } from "../../types/common";
import { useTranslation } from "react-i18next";
import { IDiagnosticReportContent, IStudyDetail } from "../../types/studies";
import { SelectChangeFunction } from "../../types/components/reactSelectSearchProps";
import { storageClassesContainOnline } from "../../helpers";
import { ShowForPermissionsCommon } from "../ShowForPermissionsCommon";
import { PERMISSIONS, ViewerDarkIcon, ViewerLightIcon } from "@advapacs/common";
import { LaddaButton as ReactLadda } from "react-ladda-button";
import { ZedIcon } from "../Icons/ZedIcon";
import LaddaButton from "../Button/LaddaButton";
import SensitiveModal from "../Study/SensitiveModal";
import { Button } from "react-bootstrap";

interface IDiagnosticReportButtons {
  contentOptions: OptionTypeBase[];
  selectedReportContent?: IDiagnosticReportContent;
  handleSelectChange: SelectChangeFunction;
  zedEnabled?: boolean;
  weasisEnabled?: boolean;
  studies?: IStudyDetail[];
  isSensitive?: boolean;
  canShowSensitive?: boolean;
  eUnityEnabled: boolean;
  viewerEnabled?: boolean;
  medDreamEnabled: boolean;
  permissions: string[];
  isShowZed?: boolean;
  isShowEUnity?: boolean;
  isShowAdvapacsViewer?: boolean;
  isShowMeddream?: boolean;
  isShowWeasis?: boolean;
  isDark?: boolean;
  advapacsViewerPermission?: string;
  openWithZed?: (ids: string[]) => void;
  openWithEUnity: (ids: string[]) => void;
  openWithMeddream: (ids: string[]) => void;
  openWithAdvapacsViewer?: (ids: string[]) => void;
  getStudyWeasis?: (studies: IStudyDetail[]) => void;
  onAdvapacsViewerLaunchClick?: () => void;
}

export const DiagnosticReportButtons: FC<IDiagnosticReportButtons> = ({
  contentOptions,
  selectedReportContent,
  openWithZed,
  getStudyWeasis,
  openWithEUnity,
  openWithMeddream,
  openWithAdvapacsViewer,
  onAdvapacsViewerLaunchClick,
  handleSelectChange,
  zedEnabled = false,
  weasisEnabled = false,
  isSensitive = false,
  canShowSensitive = false,
  eUnityEnabled = false,
  viewerEnabled = false,
  medDreamEnabled = false,
  isShowZed = true,
  isShowEUnity = true,
  isShowAdvapacsViewer = true,
  isShowMeddream = true,
  isShowWeasis = true,
  isDark = false,
  studies = [],
  permissions = [],
  advapacsViewerPermission = PERMISSIONS.VIEW_STUDY_IMAGE, // in CP , we don't use the viewStudyImage permission to open the advapacs viewer, that why we use this props to use as default is viewStudyImage for WL, PACS and viewStudy for CP.
}) => {
  const { t } = useTranslation("modal");
  const [showSensitiveStudyModal, setShowSensitiveStudyModal] = useState(false);
  const [type, setType] = useState("");
  return (
    <>
      {showSensitiveStudyModal && isSensitive && (
        <SensitiveModal
          show={showSensitiveStudyModal}
          onClose={() => {
            setShowSensitiveStudyModal(false);
          }}
          onConfirm={() => {
            if (type === "eunity") {
              openWithEUnity(studies.map((s) => s.id));
            } else if (type === "meddream") {
              openWithMeddream(studies.map((s) => s.id));
            } else if (type === "advapacViewer") {
              // @ts-ignore
              openWithAdvapacsViewer(studies.map((s) => s.id));
            } else {
              // @ts-ignore
              getStudyWeasis(studies);
            }
            setShowSensitiveStudyModal(false);
          }}
          sensitive={isSensitive}
          can={[
            PERMISSIONS.VIEW_SENSITIVE_STUDY,
            PERMISSIONS.MANAGE_SENSITIVE_STUDY,
          ]}
          permissions={permissions}
          header={t("sensitive_study")}
        />
      )}
      <div className="form-group row m-tb-xs">
        <h3 className="col-lg-4">{t("modal:diagnostic_report.report")}</h3>
        <div className="col-lg-4">
          {contentOptions &&
            (contentOptions.length > 1 ? (
              <ReactSelectSearch
                name="reportContent"
                value={
                  contentOptions?.filter(
                    (c) => c.value === selectedReportContent
                  )[0]
                }
                isClearable={false}
                options={contentOptions as OptionTypeBase[]}
                handleSelectChange={handleSelectChange}
                isSearchable={false}
                isDark={isDark}
              />
            ) : (
              <></>
            ))}
        </div>
        <div className="col-lg-4">
          <div className="list-icons flex-end">
            {zedEnabled &&
              isShowZed &&
              storageClassesContainOnline(
                studies.reduce((accumulator, currentItem) => {
                  // @ts-ignore
                  return accumulator.concat(currentItem.storageClasses);
                }, [])
              ) && (
                <ShowForPermissionsCommon
                  can={[PERMISSIONS.ACCESS_ZED_PORTAL]}
                  permissions={permissions}
                >
                  <ReactLadda
                    className="btn btn-light btn-secondary"
                    data-style="slide-right"
                    onClick={() => {
                      if (openWithZed) openWithZed(studies.map((s) => s.id));
                    }}
                    type="button"
                  >
                    <ZedIcon isColor={!isDark} width={25} height={22} />
                  </ReactLadda>
                </ShowForPermissionsCommon>
              )}
            {canShowSensitive &&
              weasisEnabled &&
              isShowWeasis &&
              storageClassesContainOnline(
                studies.reduce((accumulator, currentItem) => {
                  // @ts-ignore
                  return accumulator.concat(currentItem.storageClasses);
                }, [])
              ) && (
                <ShowForPermissionsCommon
                  can={[PERMISSIONS.ACCESS_WEASIS]}
                  permissions={permissions}
                >
                  <LaddaButton
                    style={{
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      marginBottom: -2,
                    }}
                    className="btn-light icon-Weasis"
                    onClickHandler={() => {
                      if (!getStudyWeasis) return;
                      if (isSensitive) {
                        setShowSensitiveStudyModal(true);
                        setType("weasis");
                      } else {
                        getStudyWeasis(studies);
                      }
                    }}
                    type="button"
                  />
                </ShowForPermissionsCommon>
              )}
            {canShowSensitive &&
              eUnityEnabled &&
              isShowEUnity &&
              storageClassesContainOnline(
                studies.reduce((accumulator, currentItem) => {
                  // @ts-ignore
                  return accumulator.concat(currentItem.storageClasses);
                }, [])
              ) && (
                <ShowForPermissionsCommon
                  can={[PERMISSIONS.ACCESS_EUNITY]}
                  permissions={permissions}
                >
                  <LaddaButton
                    style={{
                      fontSize: "0.875rem",
                      marginBottom: -2,
                    }}
                    className="btn-light icon-Eunity"
                    loading={false}
                    onClickHandler={() => {
                      if (isSensitive) {
                        setShowSensitiveStudyModal(true);
                        setType("eunity");
                      } else {
                        openWithEUnity(studies.map((s) => s.id));
                      }
                    }}
                    type="button"
                  />
                </ShowForPermissionsCommon>
              )}
            {canShowSensitive &&
              viewerEnabled &&
              isShowAdvapacsViewer &&
              storageClassesContainOnline(
                studies.reduce((accumulator, currentItem) => {
                  // @ts-ignore
                  return accumulator.concat(currentItem.storageClasses);
                }, [])
              ) && (
                <ShowForPermissionsCommon
                  can={[advapacsViewerPermission]}
                  permissions={permissions}
                >
                  <Button
                    style={{
                      height: 37,
                      width: 50,
                    }}
                    className="btn btn-light btn-ladda btn-ladda-spinner"
                    onClick={() => {
                      if (onAdvapacsViewerLaunchClick)
                        onAdvapacsViewerLaunchClick();
                      if (isSensitive) {
                        setShowSensitiveStudyModal(true);
                        setType("advapacViewer");
                      } else {
                        // @ts-ignore
                        openWithAdvapacsViewer(studies.map((s) => s.id));
                      }
                    }}
                    type="button"
                  >
                    <img
                      className="mb-1 advaview-icon"
                      src={isDark ? ViewerDarkIcon : ViewerLightIcon}
                      alt="AdvaView"
                    />
                  </Button>
                </ShowForPermissionsCommon>
              )}
            {canShowSensitive &&
              medDreamEnabled &&
              isShowMeddream &&
              storageClassesContainOnline(
                studies.reduce((accumulator, currentItem) => {
                  // @ts-ignore
                  return accumulator.concat(currentItem.storageClasses);
                }, [])
              ) && (
                <ShowForPermissionsCommon
                  can={[PERMISSIONS.ACCESS_MEDDREAM]}
                  permissions={permissions}
                >
                  <LaddaButton
                    style={{
                      color: "red",
                      fontSize: "0.875rem",
                      marginBottom: -2,
                    }}
                    className="btn-light icon-Meddream"
                    loading={false}
                    onClickHandler={() => {
                      if (isSensitive) {
                        setShowSensitiveStudyModal(true);
                        setType("meddream");
                      } else {
                        openWithMeddream(studies.map((s) => s.id));
                      }
                    }}
                    type="button"
                  />
                </ShowForPermissionsCommon>
              )}
          </div>
        </div>
      </div>
    </>
  );
};
